import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { setLadderRuleValue } from './fee-per-item-ladder.util';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { feePerItemValidator } from './validators/fee-per-item.validator';
import { emptyUnmatchedFee } from './unmatched-fee.util';
import { feeTypeTriggerFor, PERCENTAGE_LABEL } from './item-fee-rules/item-fee-rules.component';
import { CheckoutService } from '@pages/checkout-app/checkout.service';
import { currencyUnitsToCurrencySymbolMap } from '@pdqdev/pdq-shared/dist/utils/currency-utils';
import { CurrencyUnit } from '@pdqdev/pdq-shared/dist/entities';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';

export const FeeUnitTypeToDisplayName = new Map<Checkout.FeeUnitType, string>([
  [Checkout.FeeUnitType.ITEM, 'Item'],
  [Checkout.FeeUnitType.POUNDS, 'lb'],
]);
interface FeePerItemValue {
  enabled: boolean;
  feeRules: Checkout.FeePerItemRule[];
  byProductGroup?: boolean;
}
@Component({
  selector: 'fee-per-item',
  templateUrl: './fee-per-item.component.html',
  styleUrls: ['./fee-per-item.component.scss'],
  standalone: false,
})
export class FeePerItemComponent implements OnInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() shippingId?: number;

  feeRules: UntypedFormArray;
  groupRules: UntypedFormArray;
  FeeType = Checkout.FeeType;
  FeeUnitType = Checkout.FeeUnitType;

  readonly unmatchedOptions = [
    { value: false, label: 'No extra fee' },
    { value: true, label: 'Charge a flat fee per item' },
  ];

  unmatchedFeeTypeOptions = [
    { value: Checkout.GroupFeeType.FIXED, label: '$' },
    { value: Checkout.GroupFeeType.PERCENT_OF_ITEM_PRICE, label: '%' },
  ];

  readonly overlapOptions = [
    { value: Checkout.RuleOverlapMode.FIRST, label: 'Charge the first matching rule' },
    { value: Checkout.RuleOverlapMode.ALL, label: 'Charge every matching rule' },
  ];

  feeOptions = FeePerItemComponent.buildFeeOptions('USD');

  currencySymbol = 'USD';

  readonly feeTypeTrigger = feeTypeTriggerFor(() => this.currencySymbol);

  private static buildFeeOptions(symbol: string): DropdownOption[] {
    return [
      { value: Checkout.FeeType.PERCENTAGE, label: PERCENTAGE_LABEL, helpText: 'Of the shipping rate' },
      { value: Checkout.FeeType.FIXED_AMOUNT, label: `${symbol} Fixed amount`, helpText: 'Charged per item' },
    ];
  }
  feeUnitOptions = [
    { value: Checkout.FeeUnitType.ITEM, label: FeeUnitTypeToDisplayName.get(Checkout.FeeUnitType.ITEM) },
    { value: Checkout.FeeUnitType.POUNDS, label: FeeUnitTypeToDisplayName.get(Checkout.FeeUnitType.POUNDS) },
  ];

  destroy$ = new Subject();
  isGlobaleMode = false;

  stepAndThresholdMin = 0;
  stepAndTrhesholdPrecision = 1;
  constructor(private checkoutService: CheckoutService) {}
  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.feePerItem.setValidators(feePerItemValidator);
    this.feeRules = this.formGroup.get('feePerItem.feeRules') as UntypedFormArray;
    this.groupRules = this.formGroup.get('feePerItem.groupRules') as UntypedFormArray;
    this.feePerItem.updateValueAndValidity({ emitEvent: false });
    this.initSubscriptions();
    // Injecting it in group mode would store a ladder the merchant never configured and cannot see.
    if (!this.isGroupMode && !this.feeRules.length) {
      this.addInitialRule();
    }

    if (this.isGroupMode && !this.formGroup.get('feePerItem.overlapMode')?.value) {
      this.formGroup.get('feePerItem.overlapMode')?.setValue(Checkout.RuleOverlapMode.FIRST);
    }
  }

  get feePerItem(): UntypedFormGroup {
    return this.formGroup.get('feePerItem') as UntypedFormGroup;
  }

  get unmatchedFee(): UntypedFormGroup {
    return this.formGroup.get('feePerItem.unmatchedFee') as UntypedFormGroup;
  }

  get isGroupMode(): boolean {
    return !this.isGlobaleMode && !!this.formGroup.get('feePerItem.byProductGroup')?.value;
  }

  setMode(byProductGroup: boolean): void {
    this.formGroup.get('feePerItem.byProductGroup')?.setValue(byProductGroup);
    if (!byProductGroup && !this.feeRules.length) {
      this.addInitialRule();
    }
    const overlapMode = this.formGroup.get('feePerItem.overlapMode');
    // Null would render the picker blank.
    if (byProductGroup && !overlapMode?.value) {
      overlapMode?.setValue(Checkout.RuleOverlapMode.FIRST);
    }
    this.feePerItem.markAsDirty();
  }

  setOverlapMode(value: Checkout.RuleOverlapMode): void {
    this.formGroup.get('feePerItem.overlapMode')?.setValue(value);
  }

  get chargesUnmatched(): boolean {
    const amount = this.unmatchedFee?.get('amount')?.value;
    return amount !== null && amount !== undefined && `${amount}`.trim() !== '';
  }

  setChargesUnmatched(charges: boolean): void {
    this.unmatchedFee?.get('amount')?.setValue(charges ? 0 : null);
  }

  setUnmatchedFeeType(type: Checkout.GroupFeeType): void {
    this.unmatchedFee?.get('type')?.setValue(type);
  }

  setInputOptions(value: FeePerItemValue) {
    const firstRule = value?.feeRules?.[0];
    if (!firstRule) {
      return;
    }
    this.stepAndThresholdMin = firstRule.stepUnit === Checkout.FeeUnitType.POUNDS ? 0.1 : 1;
    this.stepAndTrhesholdPrecision = firstRule.stepUnit === Checkout.FeeUnitType.POUNDS ? 1 : 0;
  }
  initSubscriptions() {
    this.checkoutService.isGlobaleMode$.pipe(takeUntil(this.destroy$)).subscribe((isGlobaleMode) => {
      this.isGlobaleMode = isGlobaleMode;
    });

    // A FormGroup's value is always an object, so this reads the field, not the group.
    if (!this.unmatchedFee?.get('type')?.value) {
      this.unmatchedFee?.setValue(emptyUnmatchedFee(), { emitEvent: false });
    }

    this.checkoutService.shippingMethodCurrency$.pipe(takeUntil(this.destroy$)).subscribe((currency) => {
      const currencySymbol = currencyUnitsToCurrencySymbolMap[currency as CurrencyUnit] as string;
      this.currencySymbol = currencySymbol;
      this.feeOptions = FeePerItemComponent.buildFeeOptions(currencySymbol);
      this.unmatchedFeeTypeOptions = [
        { value: Checkout.GroupFeeType.FIXED, label: currencySymbol },
        { value: Checkout.GroupFeeType.PERCENT_OF_ITEM_PRICE, label: '%' },
      ];
    });

    this.formGroup
      .get('feePerItem')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.setInputOptions(value);
      });
  }
  static createFeeControl(
    amount: number | null,
    type: Checkout.FeeType,
    step: number,
    unit: Checkout.FeeUnitType,
    threshold: number,
  ) {
    return new UntypedFormGroup({
      amount: new UntypedFormControl(amount),
      type: new UntypedFormControl(type),
      step: new UntypedFormControl(step),
      stepUnit: new UntypedFormControl(unit),
      threshold: new UntypedFormControl(threshold),
    });
  }
  // What the flat fee charged before product groups existed, and what a further rule falls back to.
  private static readonly DEFAULT_FEE_AMOUNT = 0.1;

  // `missingValue` is printed against the offending row by the rules table, so repeating it down here
  // would say the same thing twice, the second time away from the row it is about.
  private static readonly ROW_LEVEL_ERRORS = ['missingValue'];

  get formLevelErrors(): string[] {
    return Object.entries(this.feePerItem?.errors ?? {})
      .filter(([key, message]) => !FeePerItemComponent.ROW_LEVEL_ERRORS.includes(key) && typeof message === 'string')
      .map(([, message]) => message as string);
  }

  toggleFeature(newEnabled: boolean) {
    this.feePerItem.get('enabled').setValue(newEnabled);
  }

  deleteRule(index: number) {
    this.feeRules.removeAt(index);
  }

  addRule() {
    const lastRule = this.feeRules.at(this.feeRules.length - 1).value;
    this.feeRules.push(
      FeePerItemComponent.createFeeControl(
        lastRule?.amount || FeePerItemComponent.DEFAULT_FEE_AMOUNT,
        lastRule?.type,
        lastRule?.step,
        lastRule?.stepUnit,
        lastRule?.threshold * 2,
      ),
    );
  }

  setValue(value: number | Checkout.FeeType | Checkout.FeeUnitType, index: number, key: string) {
    setLadderRuleValue(this.feeRules.controls, index, key, value);
  }

  // Blank, not a guessed amount: a fee the merchant never typed must never reach a shopper.
  private addInitialRule() {
    this.feeRules.push(
      FeePerItemComponent.createFeeControl(
        FeePerItemComponent.DEFAULT_FEE_AMOUNT,
        Checkout.FeeType.FIXED_AMOUNT,
        1,
        Checkout.FeeUnitType.ITEM,
        1,
      ),
    );
  }
}
